import { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import {
  GET_VARIANT_INGREDIENTS,
  GET_RAW_MATERIALS,
  CALCULATE_COGS,
} from '../../graphql/costing-queries'
import {
  UPSERT_VARIANT_INGREDIENT,
  REMOVE_VARIANT_INGREDIENT,
} from '../../graphql/costing-mutations'
import { RawMaterial, VariantIngredient } from '../../types/costing'
import { formatCost, formatCostPerUnit, formatLineCost } from '../../lib/costingUtils'
import { ProductVariant } from '../../types/product'
import LoadingSpinner from '../LoadingSpinner'
import toast from 'react-hot-toast'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'

interface Props {
  variant: ProductVariant
}

const VariantRecipeTab = ({ variant }: Props) => {
  const { hasPermission } = usePermissions()
  const canManage = hasPermission(PERMISSIONS.COSTING_MANAGE)

  const [selectedMaterialId, setSelectedMaterialId] = useState('')
  const [quantity, setQuantity] = useState('')

  const { data: ingredientsData, loading: ingredientsLoading } = useQuery(GET_VARIANT_INGREDIENTS, {
    variables: { variantId: variant.id },
  })

  const { data: allMaterialsData } = useQuery(GET_RAW_MATERIALS, {
    variables: { filters: {} },
  })

  const { data: cogsData, loading: cogsLoading } = useQuery(CALCULATE_COGS, {
    variables: { variantId: variant.id, selectedOptionItemIds: [] },
    fetchPolicy: 'cache-first',
  })

  const refetchVars = [
    { query: GET_VARIANT_INGREDIENTS, variables: { variantId: variant.id } },
    { query: CALCULATE_COGS, variables: { variantId: variant.id, selectedOptionItemIds: [] } },
  ]

  const [upsertIngredient, { loading: adding }] = useMutation(UPSERT_VARIANT_INGREDIENT, {
    refetchQueries: refetchVars,
    onCompleted: () => {
      toast.success('Ingredient added')
      setSelectedMaterialId('')
      setQuantity('')
    },
    onError: (err) => toast.error(`Failed: ${err.message}`),
  })

  const [removeIngredient] = useMutation(REMOVE_VARIANT_INGREDIENT, {
    refetchQueries: refetchVars,
    onCompleted: () => toast.success('Ingredient removed'),
    onError: (err) => toast.error(`Failed: ${err.message}`),
  })

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    const qty = parseFloat(quantity)
    if (!selectedMaterialId) { toast.error('Select an ingredient'); return }
    if (isNaN(qty) || qty <= 0) { toast.error('Enter a valid quantity'); return }
    upsertIngredient({
      variables: {
        variantId: variant.id,
        ingredient: {
          raw_material_id: parseInt(selectedMaterialId),
          quantity: qty,
        },
      },
    })
  }

  const ingredients: VariantIngredient[] = ingredientsData?.variantIngredients ?? []
  const allMaterials: RawMaterial[] = allMaterialsData?.rawMaterials ?? []
  const cogs = cogsData?.calculateCogs

  // API does not resolve the nested raw_material on variantIngredients — join client-side
  const materialById = new Map(allMaterials.map((m) => [m.id, m]))
  const getSelectedMaterial = (id: number) => materialById.get(id)

  if (ingredientsLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* COGS summary */}
      <div className={`rounded-lg border p-4 ${cogs ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
        {cogsLoading ? (
          <p className="text-sm text-gray-500">Calculating COGS…</p>
        ) : cogs === null || cogs === undefined ? (
          <p className="text-sm text-gray-500">No recipe defined. Add ingredients below to calculate COGS.</p>
        ) : (
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-gray-500">Variant cost</span>
              <p className="font-semibold text-gray-900">{formatCost(cogs.variant_cogs_millicents)}</p>
            </div>
            <div>
              <span className="text-gray-500">Total COGS</span>
              <p className="font-semibold text-gray-900">{formatCost(cogs.total_cogs_millicents)}</p>
            </div>
            <div>
              <span className="text-gray-500">Selling price</span>
              <p className="font-semibold text-gray-900">€{(variant.price_cents / 100).toFixed(2)}</p>
            </div>
            {cogs.total_cogs_millicents > 0 && variant.price_cents > 0 && (
              <div>
                <span className="text-gray-500">Gross margin</span>
                <p className="font-semibold text-gray-900">
                  {(((variant.price_cents * 1000 - cogs.total_cogs_millicents) / (variant.price_cents * 1000)) * 100).toFixed(1)}%
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Ingredients table */}
      {ingredients.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-sm text-gray-500">No ingredients defined for this variant yet.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingredient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost / unit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Line cost</th>
                {canManage && <th className="relative px-6 py-3"><span className="sr-only">Remove</span></th>}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ingredients.map((ing) => {
                const mat = materialById.get(ing.raw_material_id)
                return (
                  <tr key={ing.id}>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">
                      {mat?.name ?? `#${ing.raw_material_id}`}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700">
                      {ing.quantity}{mat ? ` ${mat.unit}` : ''}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700">
                      {mat ? formatCostPerUnit(mat.current_cost_per_unit_millicents, mat.unit) : '—'}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700">
                      {mat ? formatLineCost(mat.current_cost_per_unit_millicents, ing.quantity) : '—'}
                    </td>
                    {canManage && (
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() =>
                            removeIngredient({
                              variables: { variantId: variant.id, rawMaterialId: ing.raw_material_id },
                            })
                          }
                          className="text-gray-400 hover:text-red-600 p-1 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add ingredient form */}
      {canManage && (
        <div className="card p-6 space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Add Ingredient</h3>
          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
            <select
              value={selectedMaterialId}
              onChange={(e) => setSelectedMaterialId(e.target.value)}
              className="input flex-1"
            >
              <option value="">Select ingredient…</option>
              {allMaterials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.unit})
                </option>
              ))}
            </select>
            <div className="relative w-40">
              <input
                type="number"
                min="0"
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Qty"
                className="input w-full pr-10"
              />
              {selectedMaterialId && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  {getSelectedMaterial(parseInt(selectedMaterialId))?.unit ?? ''}
                </span>
              )}
            </div>
            <button type="submit" disabled={adding} className="btn-primary shrink-0">
              <PlusIcon className="h-4 w-4 mr-1" />
              {adding ? 'Adding…' : 'Add'}
            </button>
          </form>
        </div>
      )}
    </div>
  )
}

export default VariantRecipeTab
